<template>
  <div class="ai-reports-page">
    <div class="toolbar">
      <div class="title-block">
        <h1>AI-отчёты</h1>
        <p class="subtitle">
          Сохранённые прогоны ассистента. Повтор пересчитывает данные без LLM,
          «Обновить выводы» делает один дешёвый вызов.
        </p>
      </div>
    </div>

    <div class="layout">
      <aside class="reports-panel">
        <div v-if="loading" class="panel-hint">Загрузка…</div>
        <div v-else-if="!reports.length" class="panel-hint">
          Нет сохранённых отчётов. Спросите ассистента и нажмите
          «Сохранить как отчёт».
        </div>
        <div class="reports-list">
          <div
            v-for="report in reports"
            :key="report.id"
            class="report-item"
            :class="{ active: report.id === currentId }"
            @click="selectReport(report.id)"
          >
            <div class="report-title">{{ report.title || 'Отчёт' }}</div>
            <div class="report-meta">
              <span v-if="report.is_owner">мой</span>
              <span v-else class="shared-badge">доступен по шерингу</span>
              <span v-if="report.updated_at"> · {{ formatDateTime(report.updated_at) }}</span>
            </div>
          </div>
        </div>
      </aside>

      <section class="report-view">
        <div v-if="!current" class="empty-state">
          <p>Выберите отчёт слева</p>
        </div>
        <template v-else>
          <div class="report-header">
            <div class="report-heading">
              <h2>{{ current.title || 'Отчёт' }}</h2>
              <div class="report-subtitle">
                обновлён {{ formatDateTime(current.updated_at) }}
                <template v-if="current.last_repeat_at">
                  · последний повтор {{ formatDateTime(current.last_repeat_at) }}
                </template>
                <template v-if="lastRunInfo"> · {{ lastRunInfo }}</template>
              </div>
            </div>
            <div class="report-actions">
              <button class="action-btn" :disabled="busy" @click="repeat">
                {{ busy === 'repeat' ? 'Повтор…' : 'Повторить (0 LLM)' }}
              </button>
              <button
                v-if="current.is_owner"
                class="action-btn"
                :disabled="busy"
                @click="refresh"
              >
                {{ busy === 'refresh' ? 'Обновление…' : 'Обновить выводы' }}
              </button>
              <button
                v-if="current.is_owner"
                class="action-btn share"
                :class="{ on: current.shared }"
                :disabled="busy"
                @click="toggleShare"
              >
                {{ current.shared ? 'Отменить шеринг' : 'Поделиться' }}
              </button>
              <button
                v-if="current.is_owner"
                class="action-btn danger"
                :disabled="busy"
                @click="remove"
              >
                Удалить
              </button>
            </div>
          </div>

          <div v-if="actionError" class="action-error">{{ actionError }}</div>
          <div v-if="actionNotice" class="action-notice">{{ actionNotice }}</div>

          <div class="blocks-column">
            <AiBlock
              v-for="(block, idx) in currentBlocks"
              :key="idx"
              :block="block"
              class="report-block"
            />
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import {
  fetchReports,
  fetchReport,
  patchReport,
  deleteReport,
  repeatReport,
  refreshReport,
} from '@/backend/aiApi';
import AiBlock from './components/AiBlock.vue';
import { formatDateTime } from './format';

export default {
  name: 'AiReportsPage',
  components: { AiBlock },
  data() {
    return {
      reports: [],
      loading: false,
      currentId: null,
      current: null,
      busy: null,
      actionError: '',
      actionNotice: '',
      lastRunInfo: '',
    };
  },
  computed: {
    currentBlocks() {
      return this.current && this.current.blocks ? this.current.blocks : [];
    },
  },
  mounted() {
    this.loadReports();
  },
  methods: {
    formatDateTime,
    loadReports() {
      this.loading = true;
      return fetchReports()
        .then((reports) => {
          this.reports = reports || [];
          if (!this.currentId && this.reports.length) {
            this.selectReport(this.reports[0].id);
          }
        })
        .catch(() => { this.reports = []; })
        .finally(() => { this.loading = false; });
    },
    selectReport(id) {
      if (id === this.currentId) return;
      this.currentId = id;
      this.current = null;
      this.actionError = '';
      this.actionNotice = '';
      this.lastRunInfo = '';
      fetchReport(id)
        .then((report) => { this.current = report; })
        .catch(() => { this.actionError = 'Не удалось загрузить отчёт'; });
    },
    applyBlocks(blocks, notice) {
      this.current = { ...this.current, blocks };
      this.actionNotice = notice;
      this.actionError = '';
      this.loadReports();
    },
    repeat() {
      this.busy = 'repeat';
      this.actionError = '';
      this.actionNotice = '';
      repeatReport(this.currentId)
        .then((outcome) => {
          const errors = outcome.errors || [];
          this.lastRunInfo = `повтор: шагов ${outcome.executed}, LLM-вызовов 0`;
          this.applyBlocks(
            outcome.blocks,
            errors.length
              ? `Повтор выполнен с ошибками: ${errors.join('; ')}`
              : 'Данные пересчитаны без LLM (0 токенов)'
          );
        })
        .catch((error) => { this.actionError = `Ошибка повтора: ${error.message || error}`; })
        .finally(() => { this.busy = null; });
    },
    refresh() {
      this.busy = 'refresh';
      this.actionError = '';
      this.actionNotice = '';
      refreshReport(this.currentId)
        .then((outcome) => {
          this.lastRunInfo = `обновление выводов: 1 вызов, ${outcome.llm_tokens} токенов, ${outcome.cost_rub} руб.`;
          this.applyBlocks(outcome.blocks, 'Выводы обновлены по свежим данным');
        })
        .catch((error) => { this.actionError = `Ошибка обновления: ${error.message || error}`; })
        .finally(() => { this.busy = null; });
    },
    toggleShare() {
      this.busy = 'share';
      patchReport(this.currentId, { shared: !this.current.shared })
        .then((updated) => {
          this.current = { ...this.current, shared: updated.shared };
          this.loadReports();
        })
        .catch((error) => { this.actionError = `Ошибка: ${error.message || error}`; })
        .finally(() => { this.busy = null; });
    },
    remove() {
      this.busy = 'delete';
      deleteReport(this.currentId)
        .then(() => {
          this.currentId = null;
          this.current = null;
          this.loadReports();
        })
        .catch((error) => { this.actionError = `Ошибка удаления: ${error.message || error}`; })
        .finally(() => { this.busy = null; });
    },
  },
};
</script>

<style scoped>
.ai-reports-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  padding-bottom: 40px;
}

.toolbar {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.title-block h1 {
  font-size: 22px;
  font-weight: 600;
}

.subtitle {
  color: #6b7280;
  font-size: 13px;
  margin-top: 4px;
}

.layout {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.reports-panel {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  padding: 10px;
}

.panel-hint {
  color: #6b7280;
  font-size: 13px;
  padding: 8px;
}

.reports-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.report-item {
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.report-item:hover {
  background: #f3f4f6;
}

.report-item.active {
  background: #e5e7eb;
}

.report-title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-meta {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.shared-badge {
  color: #f79009;
}

.report-view {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 16px;
  overflow-y: auto;
}

.empty-state {
  margin: auto;
  color: #6b7280;
}

.report-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.report-heading h2 {
  font-size: 17px;
  font-weight: 600;
}

.report-subtitle {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
}

.report-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}

.action-btn:hover:not(:disabled) {
  background: #f3f4f6;
}

.action-btn.share.on {
  border-color: #f79009;
  color: #b54708;
}

.action-btn.danger {
  border-color: #f04438;
  color: #d92d20;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.action-error {
  color: #dc2626;
  font-size: 13px;
  margin-bottom: 10px;
}

.action-notice {
  color: #12b76a;
  font-size: 13px;
  margin-bottom: 10px;
}

.blocks-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.report-block {
  max-width: 900px;
}
</style>
